import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { graphqlRequest } from '../../utils/graphqlRequest';
import {
  CREATE_SUPPORT_TICKET_MESSAGE_MUTATION,
  SUPPORT_TICKET_QUERY,
  SUPPORT_TICKET_MESSAGES_QUERY,
  UPDATE_SUPPORT_TICKET_MUTATION,
} from '../../graphql/supportTicket';
import { ADMIN_ROUTES, adminPath } from '../../constants/adminPortal';
import {
  FaArrowLeft,
  FaDownload,
  FaPaperclip,
  FaRegClock,
  FaRegUserCircle,
} from 'react-icons/fa';
import '../../styles/SupportTicketResolution.css';

const BASE = adminPath(ADMIN_ROUTES.SUPPORT_MANAGEMENT);

function statusLabel(uiStatus) {
  if (uiStatus === 'IN_PROGRESS') return 'In Progress';
  if (uiStatus === 'RESOLVED') return 'Resolved';
  return 'Open';
}

function priorityLabel(uiPriority) {
  if (uiPriority === 'LOW') return 'Low';
  if (uiPriority === 'HIGH') return 'High';
  if (uiPriority === 'URGENT') return 'Urgent';
  return 'Medium';
}

function toUiStatus(apiStatus) {
  const v = String(apiStatus || '').toUpperCase();
  if (v === 'IN_PROGRESS') return 'IN_PROGRESS';
  if (v === 'RESOLVED') return 'RESOLVED';
  return 'OPEN';
}

function toUiPriority(apiPriority) {
  const v = String(apiPriority || '').toUpperCase();
  if (v === 'LOW') return 'LOW';
  if (v === 'HIGH') return 'HIGH';
  if (v === 'URGENT') return 'URGENT';
  return 'NORMAL';
}

function formatDateTime(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return '—';
  }
}

export default function SupportTicketResolution() {
  const { ticketId } = useParams();
  const [ticket, setTicket] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [reply, setReply] = useState('');
  const [adminNote, setAdminNote] = useState('');
  const [resolutionSummary, setResolutionSummary] = useState('');
  const [status, setStatus] = useState('OPEN');
  const [priority, setPriority] = useState('NORMAL');

  const load = useCallback(async () => {
    if (!ticketId) return;
    setLoading(true);
    setLoadError('');
    try {
      const data = await graphqlRequest(SUPPORT_TICKET_QUERY, { id: ticketId });
      const row = data?.supportTicket || null;
      setTicket(row);
      setStatus(toUiStatus(row?.status));
      setPriority(toUiPriority(row?.priority));

      const m = await graphqlRequest(SUPPORT_TICKET_MESSAGES_QUERY, { ticketId });
      setMessages(Array.isArray(m?.supportTicketMessages) ? m.supportTicketMessages : []);
    } catch (e) {
      setLoadError(e?.message || 'Failed to load ticket');
      setTicket(null);
      setMessages([]);
    } finally {
      setLoading(false);
    }
  }, [ticketId]);

  useEffect(() => {
    load();
  }, [load]);

  const ticketLabel = useMemo(() => {
    const n = Number(ticket?.ticketNumber);
    if (!Number.isFinite(n) || n <= 0) return ticket?.id || '';
    return `TKT-${String(n).padStart(3, '0')}`;
  }, [ticket]);

  const attachments = useMemo(() => {
    if (!Array.isArray(ticket?.attachmentFileNames)) return [];
    return ticket.attachmentFileNames
      .map((name) => String(name || '').trim())
      .filter(Boolean);
  }, [ticket]);

  const roleLabel = useMemo(() => {
    const v = String(ticket?.submittedByRole || '').trim();
    if (!v) return '—';
    return v.charAt(0).toUpperCase() + v.slice(1);
  }, [ticket]);

  const save = async () => {
    if (!ticket?.id) return;
    setSaveError('');
    setSaving(true);
    try {
      await graphqlRequest(UPDATE_SUPPORT_TICKET_MUTATION, {
        input: { id: ticket.id, status, priority },
      });
      await load();
    } catch (e) {
      setSaveError(e?.message || 'Could not update ticket');
    } finally {
      setSaving(false);
    }
  };

  const resolve = async () => {
    if (!ticket?.id) return;
    setSaveError('');
    setSaving(true);
    try {
      await graphqlRequest(UPDATE_SUPPORT_TICKET_MUTATION, {
        input: { id: ticket.id, status: 'RESOLVED', priority },
      });
      await load();
    } catch (e) {
      setSaveError(e?.message || 'Could not resolve ticket');
    } finally {
      setSaving(false);
    }
  };

  const sendReply = async () => {
    if (!ticket?.id) return;
    const content = String(reply || '').trim();
    if (!content) return;
    setSaveError('');
    setSaving(true);
    try {
      await graphqlRequest(CREATE_SUPPORT_TICKET_MESSAGE_MUTATION, {
        input: { ticketId: ticket.id, content },
      });
      setReply('');
      await load();
    } catch (e) {
      setSaveError(e?.message || 'Could not send reply');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <p className="smgmt-muted">Loading ticket…</p>;
  if (!ticket)
    return (
      <div className="ri-detail">
        <Link to={`${BASE}/tickets`} className="ri-detail-back">
          <FaArrowLeft aria-hidden /> Back
        </Link>
        <p style={{ color: '#b91c1c' }}>{loadError || 'Ticket not found'}</p>
      </div>
    );

  return (
    <div className="ticket-resolve-page">
      <Link to={`${BASE}/tickets`} className="ticket-resolve-back">
        <FaArrowLeft aria-hidden /> Back
      </Link>
      <h1 className="ticket-resolve-title">Support Ticket Resolution</h1>
      <p className="ticket-resolve-subtitle">Ticket ID: {ticketLabel}</p>

      <div className="ticket-resolve-layout">
        <aside className="ticket-resolve-left">
          <section className="ticket-resolve-card">
            <h2>Ticket Summary</h2>
            <dl>
              <dt>Ticket ID</dt>
              <dd>{ticketLabel}</dd>
              <dt>Submitted by</dt>
              <dd>{ticket.submittedByName || ticket.submittedByEmail || '—'}</dd>
              <dt>User Role</dt>
              <dd>
                <span className="ticket-chip ticket-chip--role">{roleLabel}</span>
              </dd>
              <dt>Issue Category</dt>
              <dd>{ticket.category || '—'}</dd>
              <dt>Priority Level</dt>
              <dd>
                <span className={`ticket-chip ticket-chip--priority ticket-chip--p-${priority}`}>
                  {priorityLabel(priority)}
                </span>
              </dd>
              <dt>Submission Date</dt>
              <dd>{formatDateTime(ticket.createdAt)}</dd>
              <dt>Current Status</dt>
              <dd>
                <span className={`ticket-chip ticket-chip--status ticket-chip--s-${status}`}>
                  {statusLabel(status)}
                </span>
              </dd>
            </dl>
          </section>

          <section className="ticket-resolve-card">
            <h2>Ticket Actions</h2>
            <label>
              <span>Change Status</span>
              <select value={status} onChange={(e) => setStatus(e.target.value)}>
                <option value="OPEN">Open</option>
                <option value="IN_PROGRESS">In Progress</option>
                <option value="RESOLVED">Resolved</option>
              </select>
            </label>
            <label>
              <span>Assign To</span>
              <select disabled>
                <option>Admin Mike</option>
              </select>
            </label>
            <label>
              <span>Priority Level</span>
              <select value={priority} onChange={(e) => setPriority(e.target.value)}>
                <option value="LOW">Low</option>
                <option value="NORMAL">Medium</option>
                <option value="HIGH">High</option>
                <option value="URGENT">Urgent</option>
              </select>
            </label>
            {saveError ? <p className="ri-detail-save-error">{saveError}</p> : null}
            <button
              type="button"
              className="ticket-btn ticket-btn--primary ticket-resolve-btn"
              onClick={save}
              disabled={saving}
            >
              {saving ? 'Updating…' : 'Update Ticket'}
            </button>
          </section>

          <section className="ticket-resolve-card">
            <h2>Resolution Notes</h2>
            <p className="ticket-resolve-hint">Private admin notes (not visible to user)</p>
            <textarea
              rows={4}
              value={adminNote}
              onChange={(e) => setAdminNote(e.target.value)}
              placeholder="Investigating details or handoff notes..."
            />
            <button type="button" className="ticket-btn ticket-btn--muted ticket-resolve-btn" disabled>
              Add Note
            </button>
          </section>

          <section className="ticket-resolve-card">
            <h2>Final Resolution</h2>
            <p className="ticket-resolve-hint">Provide the final solution summary</p>
            <textarea
              rows={4}
              placeholder="Resolution details..."
              value={resolutionSummary}
              onChange={(e) => setResolutionSummary(e.target.value)}
            />
            <button
              type="button"
              className="ticket-btn ticket-btn--success ticket-resolve-btn"
              onClick={resolve}
              disabled={saving || status === 'RESOLVED'}
            >
              Resolve Issue
            </button>
          </section>

          <section className="ticket-resolve-card">
            <h2>Quick Actions</h2>
            <div className="ticket-quick-actions">
              <button type="button" className="ticket-btn ticket-btn--danger" disabled>
                Escalate Issue
              </button>
              <button type="button" className="ticket-btn ticket-btn--muted" disabled>
                Mark as Spam
              </button>
              <button type="button" className="ticket-btn ticket-btn--dark" disabled>
                Close Ticket
              </button>
            </div>
          </section>
        </aside>

        <main className="ticket-resolve-right">
          <section className="ticket-resolve-card">
            <h2>Issue Report</h2>
            <h3>{ticket.subject}</h3>
            <p>{ticket.description}</p>
            <div className="ticket-resolve-divider" />
            <h4>Attachments ({attachments.length})</h4>
            {attachments.length ? (
              <div className="ticket-attachments">
                {attachments.map((name) => (
                  <div key={name} className="ticket-attachment-row">
                    <div className="ticket-attachment-left">
                      <span className="ticket-attachment-icon" aria-hidden>
                        <FaPaperclip />
                      </span>
                      <div className="ticket-attachment-meta">
                        <div className="ticket-attachment-name">{name}</div>
                        <div className="ticket-attachment-size">—</div>
                      </div>
                    </div>
                    <button type="button" className="ticket-attachment-dl" disabled aria-label="Download attachment">
                      <FaDownload aria-hidden />
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="ticket-resolve-hint">No attachments</p>
            )}
          </section>

          <section className="ticket-resolve-card">
            <h2>Conversation Thread</h2>
            {messages.length ? (
              <div className="ticket-thread-list">
                {messages.map((m) => {
                  const isAdmin =
                    String(m?.senderRole || '').toLowerCase().includes('admin');
                  return (
                    <div
                      key={m.id}
                      className={`ticket-thread-row ${
                        isAdmin ? 'ticket-thread-row--admin' : ''
                      }`}
                    >
                      <div className="ticket-thread-avatar">
                        {isAdmin ? 'A' : <FaRegUserCircle />}
                      </div>
                      <div
                        className={`ticket-thread-bubble ${
                          isAdmin ? 'ticket-thread-bubble--admin' : ''
                        }`}
                      >
                        <strong>{m.senderName || (isAdmin ? 'Admin' : 'User')}</strong>
                        <p>{m.content}</p>
                        {Array.isArray(m?.attachmentFileNames) && m.attachmentFileNames.length ? (
                          <div className="ticket-thread-files" aria-label="Message attachments">
                            {m.attachmentFileNames.map((name) => (
                              <span key={name} className="ticket-thread-file">
                                <FaPaperclip aria-hidden /> {name}
                              </span>
                            ))}
                          </div>
                        ) : null}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="ticket-resolve-hint">No messages yet.</p>
            )}

            <label className="ticket-reply-label">
              <span>Reply to User</span>
              <textarea
                rows={4}
                value={reply}
                onChange={(e) => setReply(e.target.value)}
                placeholder="Write a response or provide a solution to the user..."
              />
            </label>
            <div className="ticket-reply-actions">
              <button type="button" className="ticket-link-btn" disabled>
                <FaPaperclip aria-hidden /> Attach File
              </button>
              <button
                type="button"
                className="ticket-btn ticket-btn--primary"
                onClick={sendReply}
                disabled={saving || !reply.trim()}
              >
                Send Reply
              </button>
            </div>
          </section>

          <section className="ticket-resolve-card">
            <h2 className="ticket-history-title">
              <FaRegClock aria-hidden /> Ticket History
            </h2>
            <ul className="ticket-history-list">
              <li>
                <strong>Ticket created</strong>
                <span>{formatDateTime(ticket.createdAt)}</span>
              </li>
              <li>
                <strong>Status changed</strong>
                <span>{status.replace('_', ' ')}</span>
              </li>
              <li>
                <strong>Last updated</strong>
                <span>{formatDateTime(ticket.updatedAt)}</span>
              </li>
            </ul>
          </section>
        </main>
      </div>
    </div>
  );
}

